/**
 * ============================================================================
 * CCA COI SYSTEM - MAIN APP COMPONENT
 * Root component with routing and global providers
 * ============================================================================
 */

import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { useAuth } from './contexts/AuthContext';

// Lazy load components for better performance
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = React.lazy(() => import('./pages/auth/RegisterPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const UploadPage = React.lazy(() => import('./pages/UploadPage'));
const CertificatesPage = React.lazy(() => import('./pages/CertificatesPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Layout components
const PublicLayout = React.lazy(() => import('./layouts/PublicLayout'));
const DashboardLayout = React.lazy(() => import('./layouts/DashboardLayout'));

// Simple loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Simple error boundary
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return <div className="p-4">Something went wrong.</div>;
    }
    return this.props.children;
  }
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <Helmet>
        <title>COI System - Contractor's Choice Agency</title>
        <meta name="description" content="Generate professional Certificate of Insurance documents instantly with AI-powered contract analysis" />
        <meta name="keywords" content="certificate of insurance, contractor insurance, ACORD 25, business insurance, liability certificates" />
        <meta property="og:title" content="COI System - Contractor's Choice Agency" />
        <meta property="og:description" content="Generate professional Certificate of Insurance documents instantly" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={window.location.origin} />
      </Helmet>

      <div className="App">
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <LoadingSpinner />
          </div>
        }>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={
              user ? <Navigate to="/dashboard" replace /> : <LandingPage />
            } />
            
            <Route element={<PublicLayout />}>
              <Route path="/login" element={
                user ? <Navigate to="/dashboard" replace /> : <LoginPage />
              } />
              <Route path="/register" element={
                user ? <Navigate to="/dashboard" replace /> : <RegisterPage />
              } />
            </Route>

            {/* Protected routes */}
            <Route element={
              user ? <DashboardLayout /> : <Navigate to="/login" replace />
            }>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/upload" element={<UploadPage />} />
              <Route path="/certificates" element={<CertificatesPage />} />
              <Route path="/profile" element={<ProfilePage />} />
            </Route>

            {/* 404 page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
    </ErrorBoundary>
  );
}

export default App;